import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { IAutomationInfo, IManageAutomationModalResult, SizeConstraint } from '../../../../models';
import { ILocalNotification } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useContactAutomationsQuery } from '../../../../queries/Automation/queries/useContactAutomationsQuery';
import { brandPrimary } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { ManageAutomationModal } from '../../automation/ManageAutomation';
import { LoadingSpinner } from '../../LoadingSpinner';
import { LocalNotificationObserver } from '../../LocalNotificationObserver';
import { AutomationsIcon } from '../../svgs/icons/AutomationsIcon';
import { styleSheet } from './styles';

export interface IContactAutomationsProgressBannerComponent {
	showManageModal(automationId: string): void;
}

interface IProps extends IEventLoggingComponentProps {
	className?: string;
	contact: Api.ContactViewModel;
	customTitle?: string;
	onManageClicked?(e: React.MouseEvent<HTMLElement>, automation: Api.IAutomation): void;
	onRef?(ref?: IContactAutomationsProgressBannerComponent): void;
	sizeConstraint?: SizeConstraint;
	styles?: StyleDeclarationValue[];
}

function _ContactAutomationsProgressBanner(props: IProps) {
	const { className, contact, customTitle, onManageClicked, onRef, sizeConstraint, styles, logApiError, logEvent } =
		props;

	const contactId = contact?.id;
	const {
		data: automations,
		isLoading,
		refetch,
	} = useContactAutomationsQuery({
		contactId,
		enabled: !!contactId,
	});

	const [selectedAutomation, setSelectedAutomation] = React.useState<Api.IAutomation | null>(null);

	// Keep selectedAutomation in sync with query data when it refetches
	// This ensures the modal shows updated step data after mutations
	React.useEffect(() => {
		if (selectedAutomation && automations) {
			const updatedAutomation = automations.find(a => a.id === selectedAutomation.id);
			if (updatedAutomation && updatedAutomation !== selectedAutomation) {
				setSelectedAutomation(updatedAutomation);
			} else if (!updatedAutomation) {
				// Automation was removed (e.g., cancelled) - close the modal
				setSelectedAutomation(null);
			}
		}
	}, [automations, selectedAutomation]);

	React.useEffect(() => {
		if (contactId) {
			logEvent('LoadAutomations', { contactId });
		}
	}, [contactId, logEvent]);

	React.useEffect(() => {
		const handleRef: IContactAutomationsProgressBannerComponent = {
			showManageModal: (automationId: string) => {
				const showModal = () => {
					const automation = automations?.find(x => x.id === automationId);
					if (automation) {
						setSelectedAutomation(automation);
					}
				};
				if (isLoading) {
					refetch().then(() => {
						showModal();
					});
				} else {
					showModal();
				}
			},
		};
		onRef?.(handleRef);
		return () => {
			onRef?.(null);
		};
	}, [automations, isLoading, onRef, refetch]);

	const onNotificationReceived = React.useCallback(
		(notification?: ILocalNotification<IAutomationInfo>) => {
			if (notification?.info?.contact?.id === contactId) {
				refetch().catch((error: Api.IOperationResultNoValue) => {
					logApiError('LoadAutomations-Error', error);
				});
			}
		},
		[contactId, logApiError, refetch]
	);

	const onAutomationSelected = React.useCallback(
		(automation: Api.IAutomation) => (e: React.MouseEvent<HTMLElement>) => {
			onManageClicked?.(e, automation);
			if (!e.defaultPrevented) {
				setSelectedAutomation(automation);
			}
		},
		[onManageClicked]
	);

	const onMangeModalRequestClose = React.useCallback(
		(result?: IManageAutomationModalResult, cancel?: boolean) => {
			if (!cancel && result?.cancelled) {
				refetch();
			}
			setSelectedAutomation(null);
		},
		[refetch]
	);

	const getStatusDescription = (automation: Api.IAutomation) => {
		return automation.status?.status === Api.ProcessStatus.Started ? 'In Progress' : automation.status?.status;
	};

	const getNumberOfCompletedSteps = (automation: Api.IAutomation) => {
		return (automation.steps || []).reduce((result, x) => result + (x.isFinished ? 1 : 0), 0);
	};

	if (!isLoading && (!automations || automations.length === 0)) {
		return null;
	}

	return (
		<div
			className={`${css(styleSheet.container, ...(styles || []))} contact-automations-progress-banner ${
				className || ''
			}`}
		>
			<div className={css(baseStyleSheet.horizontalStack, styleSheet.header)}>
				<AutomationsIcon fillColor={brandPrimary} />
				<span>{customTitle || 'Automations in Progress'}</span>
			</div>
			<div>
				<div className={css(baseStyleSheet.tableColumnHeader, styleSheet.tableRow, styleSheet.tableHeader)}>
					<div className={css(styleSheet.nameCol)}>Automation Name</div>
					{sizeConstraint !== 'compact' && (
						<>
							<div className={css(styleSheet.statusCol)}>Status</div>
							<div className={css(styleSheet.stepsCol)}>Steps</div>
						</>
					)}
					<div className={css(styleSheet.actionCol)}>Action</div>
				</div>
				<ul>
					{isLoading ? (
						<li className={css(styleSheet.tableBodyRowLoading)} key='loading'>
							<LoadingSpinner type='small' />
						</li>
					) : (
						automations?.map(x => {
							const statusDescription = getStatusDescription(x);
							const numberOfCompletedSteps = getNumberOfCompletedSteps(x);
							return (
								<li className={css(styleSheet.tableRow, styleSheet.tableBodyRow)} key={x.id}>
									<div className={css(baseStyleSheet.brandLink, baseStyleSheet.truncateText, styleSheet.nameCol)}>
										<button
											className={css(baseStyleSheet.brandLink, baseStyleSheet.truncateText)}
											onClick={onAutomationSelected(x)}
										>
											<span>{x.name}</span>
										</button>
									</div>
									{sizeConstraint !== 'compact' && (
										<>
											<div className={css(baseStyleSheet.truncateText, styleSheet.statusCol)}>{statusDescription}</div>
											<div className={css(baseStyleSheet.truncateText, styleSheet.stepsCol)}>
												{x.steps?.length > 0 ? `${numberOfCompletedSteps} of ${x.steps.length} complete` : ''}
											</div>
										</>
									)}
									<button
										className={css(baseStyleSheet.brandLink, styleSheet.actionCol)}
										onClick={onAutomationSelected(x)}
									>
										<span>Manage</span>
									</button>
								</li>
							);
						})
					)}
				</ul>
			</div>
			<LocalNotificationObserver topic={Topics.CREATE_AUTOMATION} onNotificationReceived={onNotificationReceived} />
			<ManageAutomationModal
				automation={selectedAutomation}
				contact={contact?.toJs()}
				modalProps={{
					isOpen: !!selectedAutomation,
					onRequestClose: onMangeModalRequestClose,
				}}
			/>
		</div>
	);
}

export const ContactAutomationsProgressBanner = withEventLogging(
	_ContactAutomationsProgressBanner,
	'ContactAutomationsProgressBanner'
);
